import { type RefObject, useEffect } from 'react';
import { flyToExplorer, pulseBadge } from '../utils/fly-to-explorer';
import { subscribeResourceArrival } from '../utils/resource-arrival-bus';

// ~250ms at 60fps — enough for the marker to commit, paint, and settle after the thread auto-scrolls.
const MAX_FRAMES = 15;

// Fly each live-created resource's inline marker to the Context Explorer toggle, then pulse the
// count badge. Driven by the socket's live resource signal (history replay never emits), so opening
// or switching to a chat with existing resources never animates its backlog.
export const useResourceArrivals = (toggleRef: RefObject<HTMLElement | null>, badgeRef: RefObject<HTMLElement | null>) => {
  useEffect(() => subscribeResourceArrival(key => {
    // The marker mounts + lays out a frame or two after the live frame arrives (React commit,
    // then the thread auto-scrolls it into place). Poll until it's measurable rather than betting
    // on a single rAF — otherwise the flight intermittently no-ops when the marker isn't ready.
    let frames = 0;
    const tryFly = () => {
      const toggle = toggleRef.current;
      const marker = document.querySelector<HTMLElement>(`[data-resource-key="${CSS.escape(key)}"]`);
      if (marker && toggle && marker.getBoundingClientRect().width > 0) {
        void flyToExplorer(marker, toggle).then(() => {
          if (badgeRef.current) pulseBadge(badgeRef.current);
        });
        return;
      }
      // Only listed resources render a marker; if none becomes measurable within the window
      // (ui_block / agent_suggestion, or a collapsed chat with no toggle), give up quietly.
      if (frames++ < MAX_FRAMES) requestAnimationFrame(tryFly);
    };
    requestAnimationFrame(tryFly);
  }), [toggleRef, badgeRef]);
};